import { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router";
import {
  ArrowLeft,
  FileText,
  Search,
  Send,
  Plus,
  AlertCircle,
  LayoutGrid,
  List,
  User,
  MessageSquare,
  Calendar,
  Pause,
  CheckCheck,
  Play,
  Eye,
  ShieldCheck,
  CheckCircle2,
  Loader2,
  ToggleLeft,
  ToggleRight,
  Zap,
} from "lucide-react";
import { useToastStore } from "../../stores/toastStore";
import { RBACButton, RBACAction } from "../components/RBACButton";
import { PageShell } from "../components/layout/PageShell";
import { Breadcrumb } from "../components/Breadcrumb";
import {
  Modal,
  FormField,
  FormInput,
  FormTextarea,
  ModalFooterButtons,
} from "../components/Modal";
import { DataTable, type Column } from "../components/DataTable";
import { Dropdown } from "../components/Dropdown";
import { Tooltip, TooltipTrigger, TooltipContent } from "../components/ui/tooltip";
import {
  getPoam,
  listPoamItems,
  createPoamItem,
  submitMonthly,
  transitionPoamItemStatus,
  parseMilestones,
  getAssessmentPlan,
  ApiPoam,
  ApiPoamItem,
  type PoamSeverity,
} from "../../api/frameworksApi";

/* ─── Board column definitions ─── */
type BoardStatus =
  | "open"
  | "in_progress"
  | "progress_hold"
  | "verification"
  | "closed_completed"
  | "accepted_risk"
  | "completed";

const BOARD_COLUMNS: {
  key: BoardStatus;
  label: string;
  dot: string;
  /** Top-of-column color stripe (matches dot family). */
  stripe: string;
  /** Tinted background for the count chip. */
  countBg: string;
  /** Tinted text for the count chip. */
  countText: string;
  icon: React.ReactNode;
  emptyLabel: string;
}[] = [
  {
    key: "open",
    label: "Identified Finding",
    dot: "bg-red-500",
    stripe: "bg-gradient-to-r from-red-500 to-red-400",
    countBg: "bg-red-50 dark:bg-red-500/15",
    countText: "text-red-700 dark:text-red-300",
    icon: <AlertCircle className="size-3.5" />,
    emptyLabel: "No Identified Findings",
  },
  {
    key: "in_progress",
    label: "Remediation In Progress",
    dot: "bg-sky-500",
    stripe: "bg-gradient-to-r from-sky-500 to-sky-400",
    countBg: "bg-sky-50 dark:bg-sky-500/15",
    countText: "text-sky-700 dark:text-sky-300",
    icon: <Play className="size-3.5" />,
    emptyLabel: "No Remediation In Progress",
  },
  {
    key: "progress_hold",
    label: "Remediation On Hold",
    dot: "bg-amber-500",
    stripe: "bg-gradient-to-r from-amber-500 to-amber-400",
    countBg: "bg-amber-50 dark:bg-amber-500/15",
    countText: "text-amber-700 dark:text-amber-300",
    icon: <Pause className="size-3.5" />,
    emptyLabel: "No Remediation On Hold",
  },
  {
    key: "accepted_risk",
    label: "Accepted Risk",
    dot: "bg-orange-500",
    stripe: "bg-gradient-to-r from-orange-500 to-orange-400",
    countBg: "bg-orange-50 dark:bg-orange-500/15",
    countText: "text-orange-700 dark:text-orange-300",
    icon: <ShieldCheck className="size-3.5" />,
    emptyLabel: "No Accepted Risks",
  },
  {
    key: "verification",
    label: "Validation & Testing",
    dot: "bg-blue-500",
    stripe: "bg-gradient-to-r from-blue-500 to-blue-400",
    countBg: "bg-blue-50 dark:bg-blue-500/15",
    countText: "text-blue-700 dark:text-blue-300",
    icon: <Eye className="size-3.5" />,
    emptyLabel: "No Validation & Testing",
  },
  {
    key: "closed_completed",
    label: "Completed",
    dot: "bg-teal-500",
    stripe: "bg-gradient-to-r from-teal-500 to-teal-400",
    countBg: "bg-teal-50 dark:bg-teal-500/15",
    countText: "text-teal-700 dark:text-teal-300",
    icon: <CheckCircle2 className="size-3.5" />,
    emptyLabel: "No Completed Items",
  },
  {
    key: "completed",
    label: "Remediated",
    dot: "bg-emerald-500",
    stripe: "bg-gradient-to-r from-emerald-500 to-emerald-400",
    countBg: "bg-emerald-50 dark:bg-emerald-500/15",
    countText: "text-emerald-700 dark:text-emerald-300",
    icon: <CheckCheck className="size-3.5" />,
    emptyLabel: "No Remediated Items",
  },
];

const boardToApiStatus: Record<BoardStatus, string> = {
  open: "identified_finding",
  in_progress: "remediation_in_progress",
  progress_hold: "remediation_on_hold",
  verification: "validation_and_testing",
  closed_completed: "completed",
  accepted_risk: "accepted_risk",
  completed: "closed_remediated",
};

const apiStatusToBoard: Record<string, BoardStatus> = {
  identified_finding: "open",
  remediation_in_progress: "in_progress",
  remediation_on_hold: "progress_hold",
  validation_and_testing: "verification",
  completed: "closed_completed",
  accepted_risk: "accepted_risk",
  closed_remediated: "completed",
};

const STATUS_OPTIONS = [
  { value: "", label: "All Status" },
  { value: "identified_finding", label: "Identified Finding" },
  { value: "remediation_in_progress", label: "Remediation In Progress" },
  { value: "remediation_on_hold", label: "Remediation On Hold" },
  { value: "validation_and_testing", label: "Validation & Testing" },
  { value: "completed", label: "Completed" },
  { value: "accepted_risk", label: "Accepted Risk" },
  { value: "closed_remediated", label: "Remediated" },
];

/* ─── localStorage helpers for board metadata ─── */
interface ItemBoardMeta {
  status: BoardStatus;
  assignee: string;
  holdReason?: string;
  holdDate?: string;
}

function boardMetaKey(poamId: string): string {
  return `poam_board_meta_${poamId}`;
}

function loadBoardMeta(poamId: string): Record<string, ItemBoardMeta> {
  try {
    const raw = localStorage.getItem(boardMetaKey(poamId));
    return raw ? JSON.parse(raw) : {};
  } catch {
    return {};
  }
}

function saveBoardMeta(poamId: string, meta: Record<string, ItemBoardMeta>) {
  localStorage.setItem(boardMetaKey(poamId), JSON.stringify(meta));
}

function getItemMeta(
  meta: Record<string, ItemBoardMeta>,
  itemId: string,
): ItemBoardMeta {
  return meta[itemId] ?? { status: "open", assignee: "" };
}

function getRemediationCache(poamId: string, itemId: string) {
  try {
    const raw = localStorage.getItem(`poam_remediation_${poamId}_${itemId}`);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
}

function hasSavedPlan(poamId: string, itemId: string): boolean {
  return localStorage.getItem(`poam_has_plan_${poamId}_${itemId}`) === 'true';
}

const ALLOWED_TRANSITIONS: Record<BoardStatus, BoardStatus[]> = {
  open:             ['in_progress'],
  in_progress:      ['progress_hold', 'accepted_risk', 'verification'],
  progress_hold:    ['accepted_risk', 'verification'],
  accepted_risk:    ['verification', 'progress_hold'],
  verification:     ['closed_completed'],
  closed_completed: ['completed'],
  completed:        [],
};

/* ─── Kanban Card ─── */
/* ── Severity display config (shared) ── */
const SEVERITY_META: Record<PoamSeverity, { label: string; dot: string; badge: string }> = {
  critical: { label: "Critical", dot: "bg-red-500", badge: "bg-red-50 dark:bg-red-500/10 text-red-700 dark:text-red-400 border-red-200 dark:border-red-500/20" },
  high: { label: "High", dot: "bg-orange-500", badge: "bg-orange-50 dark:bg-orange-500/10 text-orange-700 dark:text-orange-400 border-orange-200 dark:border-orange-500/20" },
  medium: { label: "Medium", dot: "bg-yellow-500", badge: "bg-yellow-50 dark:bg-yellow-500/10 text-yellow-700 dark:text-yellow-500 border-yellow-200 dark:border-yellow-500/20" },
  low: { label: "Low", dot: "bg-slate-400", badge: "bg-slate-100 dark:bg-slate-500/10 text-slate-600 dark:text-slate-400 border-slate-200 dark:border-slate-500/20" },
};
/** Read-only color-coded severity badge. Severity is assigned on the item detail page. */
function SeverityBadge({ severity }: { severity?: PoamSeverity | null }) {
  /* Shared shape so all badges render at the same visual width on a single line */
  const base = "inline-flex items-center justify-center gap-1 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded border whitespace-nowrap min-w-[92px]";
  if (!severity || !SEVERITY_META[severity]) {
    return (
      <span className={`${base} bg-slate-50 dark:bg-white/[0.06] text-slate-600 dark:text-white/70 border-slate-200 dark:border-white/10`}>
        <span className="size-1.5 rounded-full bg-slate-400 dark:bg-white/40 flex-shrink-0" /> No severity
      </span>
    );
  }
  const m = SEVERITY_META[severity];
  return (
    <span className={`${base} ${m.badge}`}>
      <span className={`size-1.5 rounded-full flex-shrink-0 ${m.dot}`} /> {m.label}
    </span>
  );
}

function KanbanCard({
  item,
  poamId,
  planTitle,
  planId,
  onPlanClick,
  meta,
  onClick,
  canDrag,
  onDragStart,
}: {
  item: ApiPoamItem;
  poamId: string;
  planTitle: string;
  planId?: string;
  onPlanClick?: (planId: string) => void;
  meta: ItemBoardMeta;
  onClick: () => void;
  canDrag: boolean;
  onDragStart: (e: React.DragEvent) => void;
}) {
  const remCache = getRemediationCache(poamId, item.id);
  const milestones = remCache?.data
    ? parseMilestones(remCache.data.milestones)
    : [];
  const totalMs = milestones.length;
  const doneMs = milestones.filter((m: any) => m.completed).length;
  const daysSinceCreated = Math.floor(
    (Date.now() - new Date(item.createdAt).getTime()) / 86400000,
  );

  const severityRail = item.severity && SEVERITY_META[item.severity]
    ? SEVERITY_META[item.severity].dot
    : "bg-slate-300/70 dark:bg-white/10";
  const isAutomated = (item.affectedFrameworkControls?.length ?? 0) > 0;

  /* Prefer the assignee names from the API; fall back to the local board
     assignment. When neither has a value, the card shows "Unassigned". */
  const assigneeLabel =
    item.assigneeNames?.filter((n) => n?.trim()).join(", ") || meta.assignee || "";

  /* Hold reason comes from the API holdReason, falling back to the local board
     metadata. Shown only when a value exists. */
  const holdReasonText = item.holdReason?.trim() || meta.holdReason;

  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onClick={onClick}
      className={`group relative bg-white dark:bg-[#161629] rounded-lg border border-[#e2e8f0] dark:border-white/[0.06] ${canDrag ? 'cursor-grab active:cursor-grabbing' : 'cursor-default opacity-90'} hover:border-indigo-300 dark:hover:border-violet-500/40 hover:shadow-lg hover:shadow-indigo-500/[0.08] dark:hover:shadow-violet-500/[0.10] hover:-translate-y-0.5 transition-all duration-200 select-none`}
    >
      {/* Severity left accent rail — rounded directly so the card no longer needs overflow-hidden (lets the plan tooltip escape the card) */}
      <div className={`absolute left-0 top-0 bottom-0 w-1 rounded-l-lg ${severityRail}`} />

      <div className="p-3 pl-3.5">
        {/* Top row: type + severity chips, eye icon on the far right */}
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-center gap-1.5 flex-wrap min-w-0">
            {isAutomated ? (
              <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wide text-cyan-700 dark:text-cyan-300 bg-cyan-50 dark:bg-cyan-500/10 px-1.5 py-0.5 rounded">
                <Zap className="size-2.5" /> Automated
              </span>
            ) : item.statementId ? (
              <span className="inline-flex items-center text-[10px] font-bold tracking-wide text-violet-700 dark:text-violet-300 bg-violet-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">
                Manual
              </span>
            ) : null}
            <SeverityBadge severity={item.severity} />
          </div>

          {/* Eye icon — plan details on hover */}
          <div className="relative group/eye -mr-1 -mt-1 flex-shrink-0">
            <button
              onClick={(e) => {
                e.stopPropagation();
              }}
              className="p-1 rounded-md text-[#94a3b8] dark:text-white/40 hover:text-indigo-600 dark:hover:text-violet-300 hover:bg-indigo-50 dark:hover:bg-violet-500/10 transition-colors"
            >
              <Eye className="size-3.5" />
            </button>
            <div className="invisible group-hover/eye:visible opacity-0 group-hover/eye:opacity-100 translate-y-1 group-hover/eye:translate-y-0 transition-all duration-200 ease-out absolute right-0 top-full mt-1.5 w-max max-w-[220px] rounded-lg bg-[#0b1020]/95 backdrop-blur-md border border-white/[0.08] shadow-2xl shadow-black/50 z-50">
              {/* Trendy gradient top accent */}
              <div className="h-[2px] rounded-t-lg bg-gradient-to-r from-indigo-500 via-violet-500 to-fuchsia-500" />

              {/* Header */}
              <div className="flex items-center gap-1.5 px-2.5 py-1.5 border-b border-white/[0.06]">
                <FileText className="size-3 text-indigo-300" />
                <p className="text-[10px] font-bold tracking-[0.08em] text-white/85 whitespace-nowrap">
                  Plan Details
                </p>
              </div>

              {/* Body — label + value inline */}
              <div className="px-2.5 py-2 space-y-1.5">
                <div className="flex items-baseline gap-2">
                  <span className="text-[9px] font-bold tracking-[0.08em] text-white/45 whitespace-nowrap flex-shrink-0">
                    Plan Name
                  </span>
                  {planId && planTitle ? (
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        onPlanClick?.(planId);
                      }}
                      className="group/plan inline-flex items-center gap-1 text-left text-[12px] font-semibold text-indigo-300 hover:text-indigo-200 cursor-pointer transition-colors leading-tight min-w-0"
                    >
                      <span className="underline underline-offset-2 decoration-indigo-400/40 group-hover/plan:decoration-indigo-300 break-words">{planTitle}</span>
                      <span aria-hidden className="text-[10px] opacity-60 group-hover/plan:translate-x-0.5 transition-transform flex-shrink-0">→</span>
                    </button>
                  ) : (
                    <span className="text-[12px] font-semibold text-white/90 break-words leading-tight min-w-0">
                      {planTitle || "—"}
                    </span>
                  )}
                </div>
                {meta.assignee && (
                  <div className="flex items-baseline gap-2 pt-1.5 border-t border-white/[0.06]">
                    <span className="text-[9px] font-bold tracking-[0.08em] text-white/45 whitespace-nowrap flex-shrink-0">
                      Assignee
                    </span>
                    <span className="text-[12px] font-semibold text-white/90 break-words leading-tight min-w-0">
                      {meta.assignee}
                    </span>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Title block — clamp to 2 lines; full title still available via card click */}
        {(item.controlTitle || item.techControlStatement) && (
          <Tooltip>
            <TooltipTrigger asChild>
              <p className="mt-2 text-[13px] font-semibold text-[#0f172a] dark:text-white/95 leading-snug line-clamp-2 group-hover:text-indigo-600 dark:group-hover:text-violet-300 transition-colors break-words cursor-default">
                {item.controlTitle || item.techControlStatement}
              </p>
            </TooltipTrigger>
            <TooltipContent side="top" className="max-w-xs whitespace-normal break-words">
              {item.controlTitle || item.techControlStatement}
            </TooltipContent>
          </Tooltip>
        )}

        {(item.affectedAssetUuids?.length ?? 0) > 0 && (
          <p className="mt-1.5 text-[11px] font-medium text-[#64748b] dark:text-white/45">
            {item.affectedAssetUuids!.length} affected asset{item.affectedAssetUuids!.length !== 1 ? "s" : ""}
          </p>
        )}

        {holdReasonText && (
          <div className="mt-2.5 flex items-start gap-1.5 p-2 rounded-md bg-amber-50 dark:bg-amber-500/[0.08] border border-amber-200 dark:border-amber-500/20">
            <MessageSquare className="size-3 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <p className="text-[12px] text-amber-700 dark:text-amber-400 leading-relaxed line-clamp-3">
              {holdReasonText}
            </p>
          </div>
        )}

        {totalMs > 0 && (
          <div className="mt-2.5">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[11px] font-medium text-[#64748b] dark:text-white/60">
                Milestones
              </span>
              <span className="text-[11px] font-semibold text-[#475569] dark:text-white/70 tabular-nums">
                {doneMs}/{totalMs}
              </span>
            </div>
            <div className="h-1.5 rounded-full bg-[#f1f5f9] dark:bg-white/[0.05] overflow-hidden">
              <div
                className={`h-full rounded-full transition-all duration-500 ${doneMs === totalMs ? "bg-emerald-500" : "bg-indigo-500 dark:bg-violet-500"}`}
                style={{
                  width: `${totalMs > 0 ? (doneMs / totalMs) * 100 : 0}%`,
                }}
              />
            </div>
          </div>
        )}

        <div className="mt-2.5 pt-2 border-t border-[#f1f5f9] dark:border-white/[0.05] flex items-center justify-between gap-2">
          {assigneeLabel ? (
            <div className="flex items-center gap-1.5 min-w-0">
              <div className="size-5 rounded-full bg-gradient-to-br from-indigo-500 to-violet-500 dark:from-violet-500 dark:to-fuchsia-500 flex items-center justify-center text-[11px] font-bold text-white flex-shrink-0 shadow-sm">
                {assigneeLabel.charAt(0).toUpperCase()}
              </div>
              <span className="text-[12px] font-medium text-[#475569] dark:text-white/70 truncate">
                {assigneeLabel}
              </span>
            </div>
          ) : (
            <div className="flex items-center gap-1.5 text-[#94a3b8] dark:text-white/35 min-w-0">
              <div className="size-5 rounded-full border border-dashed border-[#cbd5e1] dark:border-white/15 flex items-center justify-center flex-shrink-0">
                <User className="size-3" />
              </div>
              <span className="text-[12px] font-medium truncate">Unassigned</span>
            </div>
          )}
          <div className="flex items-center gap-1 text-[11px] font-medium text-[#94a3b8] dark:text-white/45 flex-shrink-0 tabular-nums">
            <Calendar className="size-3" />
            {daysSinceCreated}d
          </div>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════
   Main Component
   ═══════════════════════════════════════════════════════════════ */
export function POAMDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [poam, setPoam] = useState<ApiPoam | null>(null);
  const [items, setItems] = useState<ApiPoamItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [pageSize, setPageSize] = useState(10);
  const [tableSortKey, setTableSortKey] = useState<string | null>(null);
  const [tableSortDir, setTableSortDir] = useState<'asc' | 'desc'>('asc');

  const handleTableSort = (key: string) => {
    if (tableSortKey === key) setTableSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTableSortKey(key); setTableSortDir('asc'); }
    setPage(0);
  };

  const TableSortIcon = ({ col }: { col: string }) => {
    const active = tableSortKey === col;
    return (
      <span className="inline-flex flex-col items-center ml-2 select-none" style={{ fontSize: '7px', lineHeight: '7px' }}>
        <span className={`font-black ${active && tableSortDir === 'asc' ? 'text-[#0f172a] dark:text-white' : 'text-[#94a3b8] dark:text-white/40'}`}>▲</span>
        <span className={`font-black ${active && tableSortDir === 'desc' ? 'text-[#0f172a] dark:text-white' : 'text-[#94a3b8] dark:text-white/40'}`}>▼</span>
      </span>
    );
  };

  /* Filters */
  const [selectedStatus, setSelectedStatus] = useState("");
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [filterLoading, setFilterLoading] = useState(false);

  /* View toggle — persisted in URL (?view=table) so the browser Back button
     returning from the item detail page restores whichever view the user was on. */
  const [searchParams, setSearchParams] = useSearchParams();
  const view: "board" | "table" = searchParams.get("view") === "table" ? "table" : "board";
  const setView = (next: "board" | "table") => {
    const params = new URLSearchParams(searchParams);
    if (next === "table") params.set("view", "table");
    else params.delete("view");
    setSearchParams(params, { replace: true });
  };

  /* Board metadata */
  const [boardMeta, setBoardMeta] = useState<Record<string, ItemBoardMeta>>({});

  /* Drag state */
  const [dragItemId, setDragItemId] = useState<string | null>(null);
  const [dragOverCol, setDragOverCol] = useState<BoardStatus | null>(null);

  /* Hold reason dialog */
  const [holdDialog, setHoldDialog] = useState<{
    itemId: string;
    fromStatus: BoardStatus;
  } | null>(null);
  const [holdReason, setHoldReason] = useState("");

  /* Monthly submission */
  const [showMonthly, setShowMonthly] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [monthlySuccess, setMonthlySuccess] = useState(false);

  /* Add item */
  const [showAddItem, setShowAddItem] = useState(false);
  const [newItemTitle, setNewItemTitle] = useState("");
  const [newItemDesc, setNewItemDesc] = useState("");
  const [newItemAssignee, setNewItemAssignee] = useState("");
  const [creating, setCreating] = useState(false);

  /* Plan title cache: assessmentPlanId → plan title */
  const [planTitles, setPlanTitles] = useState<Record<string, string>>({});

  /* ── Initial fetch ── */
  const fetchData = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const poamRes = await getPoam(id);
      setPoam(poamRes.data);
      let loadedItems = poamRes.data.items ?? [];
      /* The POA&M detail payload omits some per-item fields (holdReason,
         assigneeNames). Enrich them from the dedicated items endpoint so the
         cards can show the assignee names and hold reason from the API. */
      try {
        const itemsRes = await listPoamItems(id, { pageSize: 200 });
        const byId = new Map((itemsRes.data ?? []).map((i) => [i.id, i]));
        loadedItems = loadedItems.map((it) => {
          const full = byId.get(it.id);
          return full
            ? { ...it, holdReason: full.holdReason ?? it.holdReason, assigneeNames: full.assigneeNames ?? it.assigneeNames }
            : it;
        });
      } catch {
        /* non-fatal — enrichment only; base items still render */
      }
      setItems(loadedItems);
      setBoardMeta(loadBoardMeta(id));
    } catch (e: any) {
      setError(e?.message ?? "Failed to load POA&M");
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  useEffect(() => {
    if (error) useToastStore.getState().showError("Error", error);
  }, [error]);

  /* Fetch assessment plan titles for items that have assessmentPlanId */
  useEffect(() => {
    const planIds = [...new Set(items.map((i) => i.assessmentPlanId).filter(Boolean))] as string[];
    const missing = planIds.filter((pid) => !planTitles[pid]);
    if (missing.length === 0) return;
    Promise.allSettled(missing.map((pid) => getAssessmentPlan(pid))).then((results) => {
      const titles: Record<string, string> = {};
      results.forEach((r, i) => {
        if (r.status === "fulfilled" && r.value?.data?.title) {
          titles[missing[i]] = r.value.data.title;
        }
      });
      if (Object.keys(titles).length > 0) {
        setPlanTitles((prev) => ({ ...prev, ...titles }));
      }
    });
  }, [items]);

  /* ── Fetch items with filters ── */
  const fetchFilteredItems = useCallback(async () => {
    if (!id) return;
    setFilterLoading(true);
    try {
      const res = await listPoamItems(id, {
        status: selectedStatus || undefined,
        overdueOnly: overdueOnly || undefined,
      });
      setItems(res.data ?? []);
      setPage(0);
    } catch (e: any) {
      setError(e?.message ?? "Failed to load findings");
    } finally {
      setFilterLoading(false);
    }
  }, [id, selectedStatus, overdueOnly]);

  useEffect(() => {
    if (!loading) fetchFilteredItems();
  }, [selectedStatus, overdueOnly]);

  /* Update board meta and persist */
  const updateMeta = useCallback(
    (itemId: string, patch: Partial<ItemBoardMeta>) => {
      setBoardMeta((prev) => {
        const updated = {
          ...prev,
          [itemId]: { ...getItemMeta(prev, itemId), ...patch },
        };
        saveBoardMeta(id!, updated);
        return updated;
      });
    },
    [id],
  );

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const q = search.toLowerCase();
    return items.filter(
      (item) =>
        item.title.toLowerCase().includes(q) ||
        (item.description ?? "").toLowerCase().includes(q),
    );
  }, [items, search]);

  const columnItems = useMemo(() => {
    const groups: Record<BoardStatus, ApiPoamItem[]> = {
      open: [],
      in_progress: [],
      progress_hold: [],
      verification: [],
      closed_completed: [],
      accepted_risk: [],
      completed: [],
    };
    filteredItems.forEach((item) => {
      const s = apiStatusToBoard[item.status ?? "identified_finding"] ?? "open";
      groups[s].push(item);
    });
    return groups;
  }, [filteredItems]);

  const tableSortedItems = useMemo(() => {
    if (!tableSortKey) return filteredItems;
    return [...filteredItems].sort((a, b) => {
      let aVal: any = ''; let bVal: any = '';
      if (tableSortKey === 'controlId') {
        aVal = a.controlId ?? ''; bVal = b.controlId ?? '';
      } else if (tableSortKey === 'title') {
        aVal = a.title; bVal = b.title;
      } else if (tableSortKey === 'created') {
        aVal = new Date(a.createdAt).getTime(); bVal = new Date(b.createdAt).getTime();
      } else if (tableSortKey === 'updated') {
        aVal = new Date(a.updatedAt || a.createdAt).getTime(); bVal = new Date(b.updatedAt || b.createdAt).getTime();
      } else if (tableSortKey === 'status') {
        const aStatus = (a.status ?? getItemMeta(boardMeta, a.id).status === "open") ? "identified_finding" : boardToApiStatus[getItemMeta(boardMeta, a.id).status];
        const bStatus = (b.status ?? getItemMeta(boardMeta, b.id).status === "open") ? "identified_finding" : boardToApiStatus[getItemMeta(boardMeta, b.id).status];
        aVal = aStatus; bVal = bStatus;
      } else if (tableSortKey === 'severity') {
        const rank: Record<string, number> = { critical: 4, high: 3, medium: 2, low: 1 };
        aVal = rank[a.severity ?? ''] ?? 0; bVal = rank[b.severity ?? ''] ?? 0;
      } else if (tableSortKey === 'risks') {
        aVal = a.relatedRisks?.length ?? 0; bVal = b.relatedRisks?.length ?? 0;
      } else if (tableSortKey === 'observations') {
        aVal = a.relatedObservations?.length ?? 0; bVal = b.relatedObservations?.length ?? 0;
      }
      if (typeof aVal === 'number') return tableSortDir === 'asc' ? aVal - bVal : bVal - aVal;
      return tableSortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    });
  }, [filteredItems, tableSortKey, tableSortDir, boardMeta]);

  const paged = tableSortedItems.slice(page * pageSize, (page + 1) * pageSize);

  const displayTitle = (): React.ReactNode =>
    poam?.title ?? (
      <span
        aria-label="Loading POA&M title"
        className="inline-block h-4 w-44 rounded-md bg-[#e2e8f0] dark:bg-white/[0.08] animate-pulse align-middle"
      />
    );

  /* ── Drag & Drop handlers ── */
  const handleDragStart = (e: React.DragEvent, itemId: string) => {
    setDragItemId(itemId);
    e.dataTransfer.effectAllowed = "move";
    e.dataTransfer.setData("text/plain", itemId);
  };

  const handleDragOver = (e: React.DragEvent, col: BoardStatus) => {
    if (dragItemId) {
      const draggedItem = items.find(i => i.id === dragItemId);
      const currentStatus = apiStatusToBoard[draggedItem?.status ?? 'identified_finding'] ?? 'open';

      const allowed = ALLOWED_TRANSITIONS[currentStatus] ?? [];
      if (!allowed.includes(col)) {
        e.dataTransfer.dropEffect = "none";
        return;
      }
    }
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
    setDragOverCol(col);
  };

  const handleDragLeave = () => {
    setDragOverCol(null);
  };

  const handleDrop = async (e: React.DragEvent, targetCol: BoardStatus) => {
    e.preventDefault();
    setDragOverCol(null);
    const itemId = e.dataTransfer.getData("text/plain") || dragItemId;
    if (!itemId) return;
    setDragItemId(null);

    const draggedItem = items.find(i => i.id === itemId);
    const currentStatus = apiStatusToBoard[draggedItem?.status ?? 'identified_finding'] ?? 'open';

    // Same column: no-op
    if (currentStatus === targetCol) return;
    // From "Identified Finding": plan must exist before any move
    if (currentStatus === 'open' && !hasSavedPlan(id!, itemId)) return;
    // Enforce transition rules
    const allowed = ALLOWED_TRANSITIONS[currentStatus] ?? [];
    if (!allowed.includes(targetCol)) return;

    if (targetCol === "progress_hold") {
      setHoldDialog({ itemId, fromStatus: currentStatus });
      setHoldReason("");
      return;
    }

    try {
      await transitionPoamItemStatus(id!, itemId, {
        targetStatus: boardToApiStatus[targetCol],
      });
      updateMeta(itemId, {
        status: targetCol,
        holdReason: undefined,
        holdDate: undefined,
      });
      await fetchFilteredItems();
    } catch (e: any) {
      const apiMsg = e?.responseBody?.errors?.[0]?.detail || e?.responseBody?.errors?.[0]?.title || e?.message;
      setError(apiMsg ?? "Failed to update item status");
    }
  };

  const handleConfirmHold = async () => {
    if (!holdDialog || !holdReason.trim()) return;
    try {
      await transitionPoamItemStatus(id!, holdDialog.itemId, {
        targetStatus: boardToApiStatus["progress_hold"],
        reason: holdReason.trim(),
      });
      updateMeta(holdDialog.itemId, {
        status: "progress_hold",
        holdReason: holdReason.trim(),
        holdDate: new Date().toISOString(),
      });
      await fetchFilteredItems();
    } catch (e: any) {
      const apiMsg = e?.responseBody?.errors?.[0]?.detail || e?.responseBody?.errors?.[0]?.title || e?.message;
      setError(apiMsg ?? "Failed to put item on hold");
    }
    setHoldDialog(null);
    setHoldReason("");
  };

  /* ── Add Item ── */
  const handleAddItem = async () => {
    if (!newItemTitle.trim()) return;
    setCreating(true);
    try {
      const res = await createPoamItem(id!, {
        title: newItemTitle.trim(),
        ...(newItemDesc.trim() ? { description: newItemDesc.trim() } : {}),
      });
      setItems((prev) => [...prev, res.data]);
      if (newItemAssignee.trim()) {
        updateMeta(res.data.id, {
          status: "open",
          assignee: newItemAssignee.trim(),
        });
      }
      setShowAddItem(false);
      setNewItemTitle("");
      setNewItemDesc("");
      setNewItemAssignee("");
    } catch (e: any) {
      const apiMsg = e?.responseBody?.errors?.[0]?.detail || e?.responseBody?.errors?.[0]?.title || e?.message;
      setError(apiMsg ?? "Failed to add item");
    } finally {
      setCreating(false);
    }
  };

  const handleCloseAddItem = () => {
    setShowAddItem(false);
    setNewItemTitle("");
    setNewItemDesc("");
    setNewItemAssignee("");
  };

  /* ── Monthly ── */
  const handleMonthlySubmit = async () => {
    setSubmitting(true);
    try {
      await submitMonthly(id!);
      setMonthlySuccess(true);
      setTimeout(() => {
        setMonthlySuccess(false);
        setShowMonthly(false);
      }, 1800);
    } catch (e: any) {
      setError(e?.responseBody?.errors?.[0]?.detail || e?.responseBody?.errors?.[0]?.title || e?.message || "Failed to submit monthly report");
      setShowMonthly(false);
    } finally {
      setSubmitting(false);
    }
  };

  const statusBadge = (statusVal: string) => {
    const cfg: Record<
      string,
      { label: string; dot: string; bg: string; text: string }
    > = {
      identified_finding: {
        label: "Identified Finding",
        dot: "bg-red-500",
        bg: "bg-red-50 dark:bg-red-500/10",
        text: "text-red-700 dark:text-red-400",
      },
      risk_accepted: {
        label: "Risk Accepted",
        dot: "bg-orange-500",
        bg: "bg-orange-50 dark:bg-orange-500/10",
        text: "text-orange-700 dark:text-orange-400",
      },
      deviation_approved: {
        label: "Deviation Approved",
        dot: "bg-amber-500",
        bg: "bg-amber-50 dark:bg-amber-500/10",
        text: "text-amber-700 dark:text-amber-400",
      },
      closed_remediated: {
        label: "Closed Remediated",
        dot: "bg-emerald-500",
        bg: "bg-emerald-50 dark:bg-emerald-500/10",
        text: "text-emerald-700 dark:text-emerald-400",
      },
    };
    const c = cfg[statusVal] ?? {
      label: statusVal?.replace(/_/g, " ") ?? "—",
      dot: "bg-gray-400",
      bg: "bg-gray-50 dark:bg-gray-500/10",
      text: "text-gray-700 dark:text-gray-400",
    };
    return (
      <span
        className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-md text-[12px] font-bold ${c.bg} ${c.text}`}
      >
        <span className={`size-1.5 rounded-full ${c.dot}`} />
        {c.label}
      </span>
    );
  };

  /* Table columns for findings */
  const tableColumns: Column<ApiPoamItem>[] = [
    {
      key: "no",
      header: '#',
      align: "center",
      render: (_item, idx) => (
        <span className="text-[12px] text-[#334155] dark:text-white/90 font-semibold tabular-nums">
          {page * pageSize + idx + 1}
        </span>
      ),
    },
    {
      key: "controlId",
      header: <button onClick={() => handleTableSort('controlId')} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Control<TableSortIcon col="controlId" /></button>,
      align: "left",
      render: (item) => {
        const statement = item.controlTitle || item.techControlStatement;
        if (!statement && !item.controlId) {
          return <span className="text-xs text-[#94a3b8] dark:text-white/50">—</span>;
        }
        const cell = (
          <div className="flex flex-col gap-1 cursor-default">
            {/* Control ID chip first */}
            {item.controlId && (
              <div className="flex items-center gap-1.5 flex-wrap">
                <span className="text-[11px] font-semibold text-indigo-700 dark:text-indigo-300 bg-indigo-50 dark:bg-indigo-500/10 px-2 py-0.5 rounded">
                  {item.controlId?.toUpperCase()}
                </span>
                {item.statementId && (
                  <span className="text-[10px] font-mono text-[#64748b] dark:text-white/55">
                    {item.statementId?.toUpperCase()}
                  </span>
                )}
              </div>
            )}
            {/* Statement / title below */}
            {statement && (
              <p className="text-[12.5px] text-[#0f172a] dark:text-white/85 leading-snug line-clamp-2">
                {statement}
              </p>
            )}
          </div>
        );
        // Tooltip is only meaningful when there's a statement to expand
        if (!statement) return cell;
        return (
          <Tooltip>
            <TooltipTrigger asChild>{cell}</TooltipTrigger>
            <TooltipContent side="top" align="start" className="max-w-md p-2.5 space-y-1.5">
              {item.controlId && (
                <p className="text-[10px] font-bold tracking-[0.08em] text-indigo-300 dark:text-indigo-600">
                  Control Statement · {item.controlId?.toUpperCase()}
                </p>
              )}
              <p className="text-xs leading-relaxed whitespace-normal break-words">
                {statement}
              </p>
            </TooltipContent>
          </Tooltip>
        );
      },
    },
    {
      key: "type",
      header: "Type / Affects",
      align: "left",
      render: (item) => {
        const isAuto = (item.affectedFrameworkControls?.length ?? 0) > 0;
        if (isAuto) {
          /* Prefer rich frameworkControls (id + statement); fall back to plain ids */
          const fwRich: { control_id: string; statement?: string | null }[] =
            (item.frameworkControls?.length ?? 0) > 0
              ? item.frameworkControls!
              : (item.affectedFrameworkControls ?? []).map((id) => ({ control_id: id }));
          const hasAnyStatement = fwRich.some((fc) => !!fc.statement);

          const chipsBlock = (
            <div className="flex items-center gap-1 flex-wrap cursor-default">
              {fwRich.map((fc) => (
                <span key={fc.control_id} className="inline-flex items-center text-[10px] font-mono font-semibold text-indigo-600 dark:text-violet-400 bg-indigo-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded">
                  {fc.control_id?.toUpperCase()}
                </span>
              ))}
            </div>
          );

          return (
            <div className="flex flex-col gap-1">
              <span className="inline-flex items-center gap-1 text-[10px] font-bold tracking-wide text-cyan-700 dark:text-cyan-400 bg-cyan-50 dark:bg-cyan-500/10 px-1.5 py-0.5 rounded w-fit">
                <Zap className="size-2.5" /> Automated
              </span>
              {hasAnyStatement ? (
                <Tooltip>
                  <TooltipTrigger asChild>{chipsBlock}</TooltipTrigger>
                  <TooltipContent side="top" align="start" className="max-w-md p-2.5 space-y-2">
                    <p className="text-[10px] font-bold tracking-[0.08em] text-indigo-300 dark:text-indigo-600">
                      Affected Controls ({fwRich.length})
                    </p>
                    <ul className="space-y-1.5">
                      {fwRich.map((fc) => (
                        <li key={fc.control_id} className="flex items-start gap-2">
                          <span className="inline-flex items-center text-[10px] font-mono font-semibold text-indigo-200 dark:text-indigo-700 bg-white/[0.08] dark:bg-indigo-100 px-1.5 py-0.5 rounded flex-shrink-0 mt-0.5">
                            {fc.control_id?.toUpperCase()}
                          </span>
                          <p className="text-xs leading-relaxed whitespace-normal break-words flex-1">
                            {fc.statement || <span className="opacity-60 italic">No statement</span>}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </TooltipContent>
                </Tooltip>
              ) : chipsBlock}
            </div>
          );
        }
        if (item.statementId) {
          return (
            <span className="inline-flex items-center text-[10px] font-bold tracking-wide text-violet-700 dark:text-violet-400 bg-violet-50 dark:bg-violet-500/10 px-1.5 py-0.5 rounded w-fit">
              Manual
            </span>
          );
        }
        return <span className="text-xs text-[#94a3b8] dark:text-white/50">—</span>;
      },
    },
    {
      key: "status",
      header: <button onClick={() => handleTableSort('status')} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Status<TableSortIcon col="status" /></button>,
      align: "center",
      render: (item) =>
        statusBadge(
          (item.status ?? getItemMeta(boardMeta, item.id).status === "open")
            ? "identified_finding"
            : boardToApiStatus[getItemMeta(boardMeta, item.id).status],
        ),
    },
    {
      key: "severity",
      header: <button onClick={() => handleTableSort('severity')} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Severity<TableSortIcon col="severity" /></button>,
      align: "center",
      render: (item) => (
        <div className="flex justify-center">
          <SeverityBadge severity={item.severity} />
        </div>
      ),
    },
    {
      key: "description",
      header: "Summary",
      align: "left",
      tooltip: (item) => item.description?.replace(/^technical control\b/i, "Control") || '',
      render: (item) => (
        <span className="line-clamp-2 text-xs text-[#334155] dark:text-white/85">
          {item.description?.replace(/^technical control\b/i, "Control") || "—"}
        </span>
      ),
    },
    {
      key: "created",
      header: <button onClick={() => handleTableSort('created')} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Created On<TableSortIcon col="created" /></button>,
      align: "center",
      render: (item) => (
        <span className="text-xs text-[#334155] dark:text-white/85 whitespace-nowrap">
          {new Date(item.createdAt).toLocaleDateString("en-GB")}
        </span>
      ),
    },
    {
      key: "updated",
      header: <button onClick={() => handleTableSort('updated')} className="flex items-center mx-auto hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors">Updated On<TableSortIcon col="updated" /></button>,
      align: "center",
      render: (item) => (
        <span className="text-xs text-[#334155] dark:text-white/85 whitespace-nowrap">
          {new Date(item.updatedAt || item.createdAt).toLocaleDateString("en-GB")}
        </span>
      ),
    },
  ];

  return (
    /* Board view needs a full-height flex column so its kanban columns can scroll.
       Table view should hug its content (like the POA&M plans list) so the table
       card sits snugly around the rows instead of stretching and leaving a large
       empty area below — `flex-1 min-h-0` is dropped for the table view. */
    <PageShell className={view === "table" ? "flex flex-col gap-3" : "flex-1 min-h-0 flex flex-col gap-3"}>
      <Breadcrumb
        parent="POA&Ms"
        parentTo="/poam"
        current={displayTitle()}
        actions={
          <>
            <div className="flex items-center rounded-xl border border-[#e2e8f0] dark:border-white/[0.07] overflow-hidden">
              <button
                onClick={() => setView("board")}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                  view === "board"
                    ? "bg-indigo-600 dark:bg-violet-600 text-white"
                    : "text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                }`}
              >
                <LayoutGrid className="size-3.5" />
                Board
              </button>
              <button
                onClick={() => setView("table")}
                className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium transition-colors ${
                  view === "table"
                    ? "bg-indigo-600 dark:bg-violet-600 text-white"
                    : "text-[#475569] dark:text-white/60 hover:bg-[#f1f5f9] dark:hover:bg-white/[0.04]"
                }`}
              >
                <List className="size-3.5" />
                Table
              </button>
            </div>
          </>
        }
      />

      {/* Error */}
      {error && (
        <div className="flex items-center justify-center gap-2 px-4 py-3 rounded-lg border border-border bg-card text-[14px] text-[#94a3b8] dark:text-white/70">
          <span>Something went wrong.</span>
          <button
            onClick={fetchData}
            className="ml-auto text-xs underline hover:no-underline"
          >
            Retry
          </button>
        </div>
      )}

      {/* ═══ FILTER BAR ═══ */}
      {!loading && (
        <div className="flex flex-wrap items-center gap-3">
          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-[#64748b] dark:text-white/50" />
            <input
              type="text"
              placeholder="Search findings"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(0);
              }}
              className="w-full pl-9 pr-4 py-2 text-sm bg-white dark:bg-white/[0.04] border border-[#e2e8f0] dark:border-white/[0.07] rounded-xl outline-none focus:ring-2 focus:ring-indigo-500/20 dark:focus:ring-violet-500/20 focus:border-indigo-500 dark:focus:border-violet-500/50 transition-all placeholder:text-[#94a3b8] dark:placeholder:text-white/60 text-[#0f172a] dark:text-white"
            />
          </div>

          {/* Status dropdown */}
          <Dropdown
            variant="pill"
            value={selectedStatus}
            onChange={(v) => { setSelectedStatus(v); setPage(0); }}
            options={STATUS_OPTIONS.map((opt) => ({
              value: opt.value,
              label: opt.label,
              dot:
                opt.value === "completed" || opt.value === "closed_remediated"
                  ? "bg-emerald-500"
                  : opt.value === "remediation_in_progress"
                    ? "bg-indigo-500"
                    : opt.value === "validation_and_testing"
                      ? "bg-blue-500"
                      : opt.value === "remediation_on_hold"
                        ? "bg-amber-500"
                        : opt.value === "identified_finding"
                          ? "bg-red-500"
                          : opt.value === "accepted_risk"
                            ? "bg-slate-400"
                            : undefined,
            }))}
          />

          {/* Overdue toggle */}
          <button
            onClick={() => setOverdueOnly((prev) => !prev)}
            className={`flex items-center gap-2 px-4 py-2 rounded-xl border text-sm font-medium transition-all ${
              overdueOnly
                ? "bg-red-50 dark:bg-red-500/10 border-red-200 dark:border-red-500/20 text-red-700 dark:text-red-400"
                : "bg-white dark:bg-white/[0.04] border-[#e2e8f0] dark:border-white/[0.07] text-[#64748b] dark:text-white/50 hover:border-[#cbd5e1] dark:hover:border-white/[0.12]"
            }`}
          >
            {overdueOnly ? (
              <ToggleRight className="size-4 text-red-500" />
            ) : (
              <ToggleLeft className="size-4" />
            )}
            Overdue Only
          </button>

          {filterLoading && (
            <Loader2 className="size-4 animate-spin text-indigo-500 dark:text-violet-400" />
          )}
          <div className="flex items-center gap-2 ml-auto flex-shrink-0">
            <label className="text-sm text-muted-foreground whitespace-nowrap">
              Rows per page
            </label>
            <Dropdown
              value={String(pageSize)}
              onChange={(v) => {
                setPageSize(Number(v));
                setPage(0);
              }}
              options={[10, 25, 50, 100].map((n) => ({ value: String(n), label: String(n) }))}
              size="sm"
              align="end"
              className="!min-w-[80px]"
            />
          </div>
        </div>
      )}

      {/* Loading */}
      {loading && (
        <div className="flex flex-col items-center justify-center gap-3 py-16">
          <div className="size-14 rounded-2xl bg-gradient-to-br from-red-50 to-red-100 dark:from-red-500/10 dark:to-red-500/5 flex items-center justify-center shadow-sm">
            <Loader2 className="size-6 animate-spin text-red-500 dark:text-red-400" />
          </div>
          <p className="text-[14px] font-semibold text-[#475569] dark:text-white/50">
            Loading findings
          </p>
        </div>
      )}

      {/* ═══ TABLE VIEW ═══ */}
      {view === "table" && !loading && (
        <DataTable<ApiPoamItem>
          columns={tableColumns}
          data={paged}
          loading={false}
          /* Taller viewport so ~6 rows are visible before the table scrolls
             (default cap is calc(100vh - 280px), which only fit ~4 rows). */
          maxHeight="calc(100vh - 180px)"
          emptyText={
            items.length === 0
              ? "No findings in this POA&M"
              : "No findings match your filters"
          }
          emptyIcon={
            <FileText className="size-4 text-[#94a3b8] dark:text-white/50" />
          }
          onRowClick={(item) => navigate(`/poam/${id}/item/${item.id}`)}
          rowKey={(item) => item.id}
          page={page}
          pageSize={pageSize}
          totalItems={tableSortedItems.length}
          onPageChange={setPage}
          itemLabel="findings"
          hidePageButtons
        />
      )}

      {/* ═══ BOARD VIEW ═══
          Layout: board container has fixed viewport height. Columns are static
          (don't grow vertically). Each column's card-list scrolls independently.
          The board itself scrolls horizontally at the bottom — that exposed
          scrollbar is the natural "more columns →" cue. */}
      {view === "board" && !loading && (
        <div className="scrollbar-neutral flex gap-4 overflow-x-auto pb-3 -mx-1 px-1 flex-1 min-h-[420px]">
          {BOARD_COLUMNS.map((col) => {
            const colItems = columnItems[col.key] ?? [];
            const isOver = dragOverCol === col.key;
            return (
              <div
                key={col.key}
                className={`flex-shrink-0 w-[270px] h-full flex flex-col rounded-xl border transition-all ${
                  isOver
                    ? "border-indigo-400 dark:border-violet-500/70 bg-indigo-50/60 dark:bg-violet-500/[0.05] ring-2 ring-indigo-200/60 dark:ring-violet-500/20"
                    : "border-[#e2e8f0] dark:border-white/[0.06] bg-[#f8fafc] dark:bg-[#0d0d18]"
                }`}
                onDragOver={(e) => handleDragOver(e, col.key)}
                onDragLeave={handleDragLeave}
                onDrop={(e) => handleDrop(e, col.key)}
              >
                {/* Colored top stripe — rounded directly so the column no longer needs overflow-hidden (lets card tooltips escape into neighbors) */}
                {/* <div className={`h-1 rounded-t-xl ${col.stripe} flex-shrink-0`} /> */}

                {/* Sticky header — stays put while cards scroll under it */}
                <div className="flex items-center justify-between gap-2 px-3 py-2.5 bg-white/60 dark:bg-white/[0.02] border-b border-[#e2e8f0] dark:border-white/[0.05] flex-shrink-0">
                  <div className="flex items-center gap-1.5 min-w-0">
                    <span className={`size-2 rounded-full ${col.dot} flex-shrink-0 shadow-sm ring-2 ring-white dark:ring-[#0d0d18]`} />
                    <span className="text-[11.5px] font-bold tracking-wide text-[#0f172a] dark:text-white/90 whitespace-nowrap">
                      {col.label}
                    </span>
                  </div>
                  <span className={`text-[11px] font-bold tabular-nums px-1.5 py-0.5 rounded-md flex-shrink-0 ${col.countBg} ${col.countText}`}>
                    {colItems.length}
                  </span>
                </div>

                {/* Card list — only this part scrolls vertically */}
                <div className="scrollbar-neutral flex-1 min-h-0 overflow-y-auto p-2.5 space-y-2.5">
                  {colItems.length === 0 ? (
                    <div
                      className={`flex flex-col items-center justify-center h-20 rounded-lg transition-all ${
                        isOver
                          ? "bg-indigo-100/60 dark:bg-violet-500/10 ring-1 ring-indigo-300 dark:ring-violet-500/40"
                          : "bg-[#f1f5f9]/40 dark:bg-white/[0.015]"
                      }`}
                    >
                      <p className={`text-[12px] font-medium ${isOver ? "text-indigo-600 dark:text-violet-300" : "text-[#94a3b8] dark:text-white/40"}`}>
                        {isOver ? "Drop here" : col.emptyLabel}
                      </p>
                    </div>
                  ) : (
                    colItems.map((item) => (
                      <KanbanCard
                        key={item.id}
                        item={item}
                        poamId={id!}
                        planTitle={item.assessmentPlanId ? (planTitles[item.assessmentPlanId] ?? '') : ''}
                        planId={item.assessmentPlanId ?? undefined}
                        onPlanClick={(planId) => navigate(`/assessment-plans/${planId}`)}
                        meta={getItemMeta(boardMeta, item.id)}
                        onClick={() => navigate(`/poam/${id}/item/${item.id}`)}
                        canDrag={
                          col.key === 'completed'
                            ? false
                            : col.key !== 'open' || hasSavedPlan(id!, item.id)
                        }
                        onDragStart={(e) => handleDragStart(e, item.id)}
                      />
                    ))
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ═══ DIALOGS ═══ */}

      {/* Hold Reason */}
      <Modal
        open={!!holdDialog}
        onClose={() => {
          setHoldDialog(null);
          setHoldReason("");
        }}
        title="Hold Reason"
        description="Provide a reason for putting this item on hold"
        footer={
          <ModalFooterButtons
            onCancel={() => {
              setHoldDialog(null);
              setHoldReason("");
            }}
            onSubmit={handleConfirmHold}
            submitLabel="Confirm Hold"
            submitDisabled={!holdReason.trim()}
          />
        }
      >
        <div className="p-3 rounded-lg bg-amber-50 dark:bg-amber-500/[0.08] border border-amber-200 dark:border-amber-500/20 text-xs text-amber-700 dark:text-amber-400 leading-relaxed">
          Moving this item to <strong>Progress Hold</strong> will pause active
          remediation.
        </div>
        <FormField label="Reason for Hold" required>
          <FormTextarea
            placeholder="e.g. Waiting for vendor patch, resource unavailable"
            value={holdReason}
            onChange={setHoldReason}
            rows={3}
          />
        </FormField>
      </Modal>

      {/* Monthly Submission */}
      <Modal
        open={showMonthly}
        onClose={() => setShowMonthly(false)}
        title="Submit Monthly Report"
        description="Create a snapshot of the current POA&M status"
        footer={
          monthlySuccess ? undefined : (
            <ModalFooterButtons
              onCancel={() => setShowMonthly(false)}
              onSubmit={handleMonthlySubmit}
              submitLabel={submitting ? "Submitting" : "Submit Report"}
              submitDisabled={submitting}
            />
          )
        }
      >
        {monthlySuccess ? (
          <div className="flex flex-col items-center gap-3 py-4">
            <div className="size-12 rounded-full bg-emerald-100 dark:bg-emerald-500/20 flex items-center justify-center">
              <CheckCircle2 className="size-5 text-emerald-600 dark:text-emerald-400" />
            </div>
            <p className="text-sm font-semibold text-emerald-700 dark:text-emerald-400">
              Monthly report submitted successfully
            </p>
          </div>
        ) : (
          <div className="p-4 rounded-lg bg-[#f5f7fb] dark:bg-white/[0.03] border border-[#e2e8f0] dark:border-white/[0.06] text-sm text-[#475569] dark:text-white/60 space-y-2">
            <p>
              This will create a snapshot of the current POA&amp;M status
              including all items and their remediation progress.
            </p>
          </div>
        )}
      </Modal>

      {/* Add Finding */}
      <Modal
        open={showAddItem}
        onClose={handleCloseAddItem}
        title="Add Finding"
        description="Add a new identified finding to this POA&M"
        footer={
          <ModalFooterButtons
            onCancel={handleCloseAddItem}
            onSubmit={handleAddItem}
            submitLabel={creating ? "Saving" : "Save"}
            submitDisabled={!newItemTitle.trim() || creating}
          />
        }
      >
        <FormField label="Title" required>
          <FormInput
            placeholder="e.g. DB encryption key rotation overdue"
            value={newItemTitle}
            onChange={setNewItemTitle}
            disabled={creating}
          />
        </FormField>
        <FormField label="Description">
          <FormTextarea
            placeholder="Describe the finding or issue"
            value={newItemDesc}
            onChange={setNewItemDesc}
            rows={3}
          />
        </FormField>
        <FormField label="Assignee">
          <FormInput
            placeholder="e.g. John Doe"
            value={newItemAssignee}
            onChange={setNewItemAssignee}
            disabled={creating}
          />
        </FormField>
      </Modal>
    </PageShell>
  );
}
